import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  createTelephonyProfileMutation,
  listTelephonyProfilesQueryKey,
  marketLabels,
  updateTelephonyProfileMutation,
  useTelephonyProfile,
} from '@/entities/telephony'
import { cn, errorMessage } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { SelectField, TextField } from '@/shared/ui/field'
import { SlideOver } from '@/shared/ui/slide-over'

interface ProfileFormProps {
  open: boolean
  // id профиля переводит форму в режим правки; без него — заведение нового
  profileId?: string
  onClose: () => void
}

export function ProfileForm({ open, profileId, onClose }: ProfileFormProps) {
  const qc = useQueryClient()
  const { profile } = useTelephonyProfile(profileId ?? '')
  const mode = profileId ? 'edit' : 'create'
  const [registrarHost, registrarPort] = (profile?.registrar ?? ':').split(':')

  const [form, setForm] = useState({
    id: profile?.id ?? '',
    name: profile?.name ?? '',
    market: profile?.market ?? 'ru',
    host: registrarHost ?? '',
    port: registrarPort || '5060',
    transport: profile?.transport ?? 'udp',
    authMode: profile?.auth_mode ?? 'register',
    regExpires: String(profile?.reg_expires_sec ?? 3600),
    codecs: (profile?.codecs ?? ['PCMA', 'PCMU']).join(', '),
    dtmf: profile?.dtmf_mode ?? 'rfc2833',
    keepalive: String(profile?.nat_keepalive_sec ?? 25),
    channels: String(profile?.channels_per_account ?? 4),
    media: profile?.media_security ?? 'rtp',
    marking: profile?.supports_marking ?? false,
    apiProvisioning: profile?.api_provisioning ?? false,
  })

  const onDone = () => {
    void qc.invalidateQueries({ queryKey: listTelephonyProfilesQueryKey() })
    onClose()
  }
  const create = useMutation({ ...createTelephonyProfileMutation(), onSuccess: onDone })
  const update = useMutation({ ...updateTelephonyProfileMutation(), onSuccess: onDone })
  const pending = create.isPending || update.isPending
  const error = create.error ?? update.error

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))

  const body = () => ({
    name: form.name.trim(),
    market: form.market as 'ru' | 'sa' | 'eu',
    registrar_host: form.host.trim(),
    registrar_port: Number(form.port),
    transport: form.transport as 'udp' | 'tcp' | 'tls',
    auth_mode: form.authMode as 'register' | 'invite',
    reg_expires_sec: Number(form.regExpires) || 0,
    codecs: form.codecs
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean),
    dtmf_mode: form.dtmf.trim(),
    nat_keepalive_sec: Number(form.keepalive) || 0,
    channels_per_account: Number(form.channels) || 0,
    media_security: form.media as 'rtp' | 'srtp',
    supports_marking: form.marking,
    api_provisioning: form.apiProvisioning,
  })

  const valid =
    form.name.trim() &&
    form.host.trim() &&
    Number(form.port) > 0 &&
    form.codecs.trim() &&
    (mode === 'edit' || form.id.trim())

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Новый профиль оператора' : `Профиль ${profile?.name ?? ''}`}
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            pending={pending}
            disabled={!valid}
            onClick={() => {
              if (mode === 'create') {
                create.mutate({ body: { id: form.id.trim(), ...body() } })
              } else if (profileId) {
                update.mutate({ path: { id: profileId }, body: body() })
              }
            }}
          >
            {mode === 'create' ? 'Создать' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mode === 'create' && (
          <TextField
            label="ID"
            required
            placeholder="mtt"
            value={form.id}
            onChange={(e) => set({ id: e.target.value })}
          />
        )}
        <TextField
          label="Название"
          required
          placeholder="МТТ"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />
        <SelectField
          label="Рынок"
          required
          value={form.market}
          onChange={(e) => set({ market: e.target.value })}
        >
          {Object.entries(marketLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
        <div className="grid grid-cols-[1fr_110px] gap-3">
          <TextField
            label="Registrar host"
            required
            placeholder="sip.mtt.ru"
            value={form.host}
            onChange={(e) => set({ host: e.target.value })}
          />
          <TextField
            label="Порт"
            required
            type="number"
            value={form.port}
            onChange={(e) => set({ port: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Транспорт"
            value={form.transport}
            onChange={(e) => set({ transport: e.target.value })}
          >
            <option value="udp">UDP</option>
            <option value="tcp">TCP</option>
            <option value="tls">TLS</option>
          </SelectField>
          <SelectField
            label="Auth"
            value={form.authMode}
            onChange={(e) => set({ authMode: e.target.value })}
          >
            <option value="register">REGISTER</option>
            <option value="invite">по INVITE</option>
          </SelectField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Медиа"
            value={form.media}
            onChange={(e) => set({ media: e.target.value })}
          >
            <option value="rtp">RTP</option>
            <option value="srtp">SRTP</option>
          </SelectField>
          <TextField
            label="DTMF"
            value={form.dtmf}
            onChange={(e) => set({ dtmf: e.target.value })}
          />
        </div>
        <TextField
          label="Кодеки"
          required
          value={form.codecs}
          onChange={(e) => set({ codecs: e.target.value })}
          hint="Через запятую, в порядке приоритета"
        />
        <div className="grid grid-cols-3 gap-3">
          <TextField
            label="Expires, с"
            type="number"
            value={form.regExpires}
            onChange={(e) => set({ regExpires: e.target.value })}
          />
          <TextField
            label="Keepalive, с"
            type="number"
            value={form.keepalive}
            onChange={(e) => set({ keepalive: e.target.value })}
          />
          <TextField
            label="Каналы"
            type="number"
            value={form.channels}
            onChange={(e) => set({ channels: e.target.value })}
          />
        </div>
        <ToggleRow
          label="Маркировка звонков"
          checked={form.marking}
          onChange={(v) => set({ marking: v })}
        />
        <ToggleRow
          label="API-провижн учёток"
          checked={form.apiProvisioning}
          onChange={(v) => set({ apiProvisioning: v })}
        />
        {error != null && (
          <p className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
            {errorMessage(error)}
          </p>
        )}
      </div>
    </SlideOver>
  )
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-control bg-raise px-4 py-3">
      <span className="text-body-sm">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          'relative h-6 w-10 rounded-full transition-colors',
          checked ? 'bg-accent' : 'bg-outline',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-5 rounded-full bg-white transition-transform',
            checked ? 'translate-x-[18px]' : 'translate-x-0.5',
          )}
        />
      </span>
    </label>
  )
}
